import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SMCCAsset, type SMCCUGCSubmission } from '../../../services/api';
import { useToast } from '../../../components/common/Toast';

type AssetType = 'image' | 'video' | 'gif' | 'audio' | 'all';
type ViewMode = 'grid' | 'list';
type Tab = 'assets' | 'ugc';



const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getAssetTypeIcon = (type: string) => {
  switch (type) {
    case 'image':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    case 'video':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    case 'gif':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
        </svg>
      );
    case 'audio':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
  }
};

const getPlatformIcon = (platform: string) => {
  const iconClass = "w-4 h-4";
  switch (platform.toLowerCase()) {
    case 'instagram':
      return <span className={`${iconClass} text-pink-500`}>IG</span>;
    case 'tiktok':
      return <span className={`${iconClass} text-gray-800`}>TT</span>;
    case 'twitter':
    case 'x':
      return <span className={`${iconClass} text-blue-400`}>X</span>;
    case 'facebook':
      return <span className={`${iconClass} text-blue-600`}>FB</span>;
    default:
      return <span className={iconClass}>{platform.slice(0, 2).toUpperCase()}</span>;
  }
};

const getConsentStatusBadge = (status: string) => {
  switch (status) {
    case 'approved':
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">Approved</span>;
    case 'denied':
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">Denied</span>;
    case 'requested':
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Requested</span>;
    default:
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Pending</span>;
  }
};

export default function SMCCAssetLibrary() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<Tab>('assets');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [assetTypeFilter, setAssetTypeFilter] = useState<AssetType>('all');
  const [currentFolder, setCurrentFolder] = useState('/');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [, setShowUploadModal] = useState(false);
  const [showAssetModal, setShowAssetModal] = useState<SMCCAsset | null>(null);
  const [showUGCModal, setShowUGCModal] = useState<SMCCUGCSubmission | null>(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [ugcConsentFilter, setUgcConsentFilter] = useState<string>('all');

  // Fetch assets
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['smcc-assets', assetTypeFilter, currentFolder, searchQuery],
    queryFn: () => api.getSMCCAssets({
      asset_type: assetTypeFilter !== 'all' ? assetTypeFilter : undefined,
      folder_path: currentFolder,
      search: searchQuery || undefined,
      limit: 100,
    }),
    enabled: activeTab === 'assets',
  });

  // Fetch folders
  const { data: foldersData } = useQuery({
    queryKey: ['smcc-asset-folders'],
    queryFn: () => api.getSMCCAssetFolders(),
    enabled: activeTab === 'assets',
  });

  // Fetch UGC
  const { data: ugcData, isLoading: ugcLoading } = useQuery({
    queryKey: ['smcc-ugc', ugcConsentFilter, searchQuery],
    queryFn: () => api.getSMCCUGC({
      consent_status: ugcConsentFilter !== 'all' ? ugcConsentFilter : undefined,
      search: searchQuery || undefined,
      limit: 100,
    }),
    enabled: activeTab === 'ugc',
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => api.uploadSMCCAsset(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-assets'] });
      queryClient.invalidateQueries({ queryKey: ['smcc-asset-folders'] });
      setShowUploadModal(false);
      addToast('success', 'Asset uploaded successfully');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Failed to upload asset');
    },
  });

  // Delete asset mutation
  const deleteAssetMutation = useMutation({
    mutationFn: (id: string) => api.deleteSMCCAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-assets'] });
      setShowAssetModal(null);
      addToast('success', 'Asset deleted');
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteSMCCAssets(ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smcc-assets'] });
      setSelectedAssets(new Set());
      addToast('success', `Deleted ${data.deleted} assets`);
    },
  });

  // Bulk move mutation
  const bulkMoveMutation = useMutation({
    mutationFn: ({ ids, folder }: { ids: string[]; folder: string }) => api.bulkMoveSMCCAssets(ids, folder),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smcc-assets'] });
      setSelectedAssets(new Set());
      addToast('success', `Moved ${data.moved} assets`);
    },
  });

  // Create folder mutation
  const createFolderMutation = useMutation({
    mutationFn: (path: string) => api.createSMCCAssetFolder(path),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-asset-folders'] });
      setShowNewFolderModal(false);
      setNewFolderName('');
      addToast('success', 'Folder created');
    },
  });

  // UGC consent request mutation
  const requestConsentMutation = useMutation({
    mutationFn: (id: string) => api.requestSMCCUGCConsent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-ugc'] });
      addToast('success', 'Consent request sent');
    },
  });

  // UGC status update mutation
  const updateUGCStatusMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { consent_status?: string; is_featured?: boolean } }) => api.updateSMCCUGCStatus(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-ugc'] });
      setShowUGCModal(null);
      addToast('success', 'UGC status updated');
    },
  });

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    formData.append('folder_path', currentFolder);

    uploadMutation.mutate(formData);
  }, [currentFolder, uploadMutation]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const toggleAssetSelection = (id: string) => {
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedAssets(newSelected);
  };

  const selectAll = () => {
    if (assetsData?.assets) {
      setSelectedAssets(new Set(assetsData.assets.map(a => a.id)));
    }
  };

  const deselectAll = () => {
    setSelectedAssets(new Set());
  };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const folderPath = currentFolder === '/'
      ? `/${newFolderName.trim()}`
      : `${currentFolder}/${newFolderName.trim()}`;
    createFolderMutation.mutate(folderPath);
  };

  const assets = assetsData?.assets || [];
  const folders = foldersData?.folders || [];
  const ugcSubmissions = ugcData?.submissions || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asset Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage media assets and user-generated content
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,audio/*"
            onChange={(e) => handleFileUpload(e.target.files)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('assets')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assets'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Assets
            {assetsData?.total !== undefined && (
              <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                {assetsData.total}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('ugc')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ugc'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            User-Generated Content
            {ugcData?.total !== undefined && (
              <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                {ugcData.total}
              </span>
            )}
          </button>
        </nav>
      </div>

      {activeTab === 'assets' && (
        <div className="flex gap-6">
          {/* Sidebar - Folders */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900">Folders</h3>
                <button
                  onClick={() => setShowNewFolderModal(true)}
                  className="text-gray-400 hover:text-gray-600"
                  title="New folder"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => setCurrentFolder('/')}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg ${
                    currentFolder === '/'
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    <span>All Assets</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {folders.reduce((sum, f) => sum + f.count, 0)}
                  </span>
                </button>
                {folders.filter(f => f.path !== '/').map((folder) => (
                  <button
                    key={folder.path}
                    onClick={() => setCurrentFolder(folder.path)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg ${
                      currentFolder === folder.path
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      <span className="truncate">{folder.path.split('/').pop()}</span>
                    </div>
                    <span className="text-xs text-gray-400">{folder.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1">
                  {/* Search */}
                  <div className="relative flex-1 max-w-md">
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  {/* Type filter */}
                  <select
                    value={assetTypeFilter}
                    onChange={(e) => setAssetTypeFilter(e.target.value as AssetType)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Types</option>
                    <option value="image">Images</option>
                    <option value="video">Videos</option>
                    <option value="gif">GIFs</option>
                    <option value="audio">Audio</option>
                  </select>
                </div>

                {/* View toggle */}
                <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Bulk actions */}
              {selectedAssets.size > 0 && (
                <div className="mt-4 flex items-center gap-4 pt-4 border-t border-gray-200">
                  <span className="text-sm text-gray-600">
                    {selectedAssets.size} selected
                  </span>
                  <button
                    onClick={deselectAll}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Deselect all
                  </button>
                  <div className="flex-1" />
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        bulkMoveMutation.mutate({
                          ids: Array.from(selectedAssets),
                          folder: e.target.value,
                        });
                        e.target.value = '';
                      }
                    }}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                  >
                    <option value="">Move to...</option>
                    <option value="/">Root</option>
                    {folders.filter(f => f.path !== '/').map((folder) => (
                      <option key={folder.path} value={folder.path}>
                        {folder.path}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => bulkDeleteMutation.mutate(Array.from(selectedAssets))}
                    className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8 text-center mb-4 hover:border-primary-400 transition-colors"
            >
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="mt-2 text-sm text-gray-600">
                Drag and drop files here, or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 hover:text-primary-500 font-medium"
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Images, videos, GIFs, and audio files supported
              </p>
            </div>

            {/* Assets Grid/List */}
            {assetsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : assets.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No assets</h3>
                <p className="mt-1 text-sm text-gray-500">Upload files to get started</p>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {assets.map((asset: SMCCAsset) => (
                  <div
                    key={asset.id}
                    className={`group relative bg-white rounded-lg border overflow-hidden transition-shadow hover:shadow-md ${
                      selectedAssets.has(asset.id) ? 'ring-2 ring-primary-500' : 'border-gray-200'
                    }`}
                  >
                    {/* Checkbox */}
                    <div className="absolute top-2 left-2 z-10">
                      <input
                        type="checkbox"
                        checked={selectedAssets.has(asset.id)}
                        onChange={() => toggleAssetSelection(asset.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>

                    {/* Preview */}
                    <button
                      type="button"
                      onClick={() => setShowAssetModal(asset)}
                      className="w-full aspect-square bg-gray-100 flex items-center justify-center overflow-hidden"
                    >
                      {asset.asset_type === 'image' || asset.asset_type === 'gif' ? (
                        <img
                          src={asset.thumbnail_url || asset.file_url}
                          alt={asset.alt_text || asset.name}
                          className="w-full h-full object-cover"
                        />
                      ) : asset.asset_type === 'video' ? (
                        <div className="relative w-full h-full">
                          {asset.thumbnail_url ? (
                            <img
                              src={asset.thumbnail_url}
                              alt={asset.name || 'Unnamed'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              {getAssetTypeIcon('video')}
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-400">
                          {getAssetTypeIcon(asset.asset_type)}
                        </div>
                      )}
                    </button>

                    {/* Info */}
                    <div className="p-3">
                      <p className="text-sm font-medium text-gray-900 truncate">{asset.name || 'Unnamed'}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          {getAssetTypeIcon(asset.asset_type)}
                          {asset.asset_type}
                        </span>
                        <span>{formatFileSize(asset.file_size || 0)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedAssets.size === assets.length && assets.length > 0}
                          onChange={(e) => e.target.checked ? selectAll() : deselectAll()}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folder</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Used</th>
                      <th className="w-12 px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {assets.map((asset: SMCCAsset) => (
                      <tr
                        key={asset.id}
                        className={`hover:bg-gray-50 ${selectedAssets.has(asset.id) ? 'bg-primary-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedAssets.has(asset.id)}
                            onChange={() => toggleAssetSelection(asset.id)}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setShowAssetModal(asset)}
                            className="flex items-center gap-3 cursor-pointer"
                          >
                            <div className="w-10 h-10 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                              {asset.asset_type === 'image' || asset.asset_type === 'gif' ? (
                                <img src={asset.thumbnail_url || asset.file_url} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  {getAssetTypeIcon(asset.asset_type)}
                                </div>
                              )}
                            </div>
                            <span className="font-medium text-gray-900">{asset.name || 'Unnamed'}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 capitalize">{asset.asset_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatFileSize(asset.file_size || 0)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{asset.folder_path}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{asset.use_count}x</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setShowAssetModal(asset)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'ugc' && (
        <div>
          {/* UGC Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  placeholder="Search by creator or caption..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <select
                value={ugcConsentFilter}
                onChange={(e) => setUgcConsentFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="requested">Requested</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
              </select>
            </div>
          </div>

          {/* UGC Grid */}
          {ugcLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : ugcSubmissions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No UGC submissions</h3>
              <p className="mt-1 text-sm text-gray-500">User-generated content will appear here when detected</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ugcSubmissions.map((ugc: SMCCUGCSubmission) => (
                <button
                  type="button"
                  key={ugc.id}
                  onClick={() => setShowUGCModal(ugc)}
                  className="text-left bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                >
                  {/* Preview */}
                  <div className="aspect-video bg-gray-100 relative">
                    {ugc.media_urls?.length > 0 && (
                      <img
                        src={ugc.media_urls[0]?.url}
                        alt={ugc.caption || 'UGC'}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <div className="absolute top-2 right-2 flex items-center gap-2">
                      {getPlatformIcon(ugc.platform)}
                      {ugc.is_featured && (
                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          Featured
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">@{ugc.creator_username}</span>
                      </div>
                      {getConsentStatusBadge(ugc.consent_status)}
                    </div>
                    {ugc.caption && (
                      <p className="text-sm text-gray-600 line-clamp-2">{ugc.caption}</p>
                    )}
                    <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                      <span>Detected {new Date(ugc.detected_at).toLocaleDateString()}</span>
                      {ugc.quality_score !== undefined && (
                        <span>Quality: {ugc.quality_score}/100</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Asset Detail Modal */}
      {showAssetModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowAssetModal(null)}
              onKeyDown={(e) => e.key === 'Escape' && setShowAssetModal(null)}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
              <div className="flex">
                {/* Preview */}
                <div className="w-1/2 bg-gray-900 flex items-center justify-center p-4">
                  {showAssetModal.asset_type === 'image' || showAssetModal.asset_type === 'gif' ? (
                    <img
                      src={showAssetModal.file_url}
                      alt={showAssetModal.alt_text || showAssetModal.name}
                      className="max-w-full max-h-[70vh] object-contain"
                    />
                  ) : showAssetModal.asset_type === 'video' ? (
                    <video
                      src={showAssetModal.file_url}
                      controls
                      className="max-w-full max-h-[70vh]"
                    />
                  ) : (
                    <audio src={showAssetModal.file_url} controls className="w-full" />
                  )}
                </div>

                {/* Details */}
                <div className="w-1/2 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">{showAssetModal.name}</h3>
                    <button
                      onClick={() => setShowAssetModal(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <span className="block text-sm font-medium text-gray-700">Type</span>
                      <p className="mt-1 text-sm text-gray-900 capitalize">{showAssetModal.asset_type}</p>
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-gray-700">Size</span>
                      <p className="mt-1 text-sm text-gray-900">{formatFileSize(showAssetModal.file_size ?? 0)}</p>
                    </div>
                    {showAssetModal.dimensions && (
                      <div>
                        <span className="block text-sm font-medium text-gray-700">Dimensions</span>
                        <p className="mt-1 text-sm text-gray-900">
                          {showAssetModal.dimensions.width} x {showAssetModal.dimensions.height}
                        </p>
                      </div>
                    )}
                    {showAssetModal.description && (
                      <div>
                        <span className="block text-sm font-medium text-gray-700">Description</span>
                        <p className="mt-1 text-sm text-gray-900">{showAssetModal.description}</p>
                      </div>
                    )}
                    {showAssetModal.tags?.length > 0 && (
                      <div>
                        <span className="block text-sm font-medium text-gray-700">Tags</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {showAssetModal.tags.map((tag) => (
                            <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <span className="block text-sm font-medium text-gray-700">Folder</span>
                      <p className="mt-1 text-sm text-gray-900">{showAssetModal.folder_path}</p>
                    </div>
                    <div>
                      <span className="block text-sm font-medium text-gray-700">Used</span>
                      <p className="mt-1 text-sm text-gray-900">{showAssetModal.use_count} times</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200 flex gap-3">
                    <a
                      href={showAssetModal.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 text-center"
                    >
                      Open Original
                    </a>
                    <button
                      onClick={() => {
                        if (confirm('Delete this asset?')) {
                          deleteAssetMutation.mutate(showAssetModal.id);
                        }
                      }}
                      className="px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UGC Detail Modal */}
      {showUGCModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowUGCModal(null)}
              onKeyDown={(e) => e.key === 'Escape' && setShowUGCModal(null)}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {getPlatformIcon(showUGCModal.platform)}
                    <span className="font-medium text-gray-900">@{showUGCModal.creator_username}</span>
                    {getConsentStatusBadge(showUGCModal.consent_status)}
                  </div>
                  <button
                    onClick={() => setShowUGCModal(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {showUGCModal.media_urls?.length > 0 && (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-4">
                    <img
                      src={showUGCModal.media_urls[0]?.url}
                      alt={showUGCModal.caption || 'UGC'}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {showUGCModal.caption && (
                  <p className="text-sm text-gray-700 mb-4">{showUGCModal.caption}</p>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                  <div>
                    <span className="text-gray-500">Detected:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(showUGCModal.detected_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Via:</span>
                    <span className="ml-2 text-gray-900 capitalize">{showUGCModal.detected_via}</span>
                  </div>
                  {showUGCModal.quality_score !== undefined && (
                    <div>
                      <span className="text-gray-500">Quality Score:</span>
                      <span className="ml-2 text-gray-900">{showUGCModal.quality_score}/100</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <a
                    href={showUGCModal.post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 text-center"
                  >
                    View Original Post
                  </a>
                  {showUGCModal.consent_status === 'pending' && (
                    <button
                      onClick={() => requestConsentMutation.mutate(showUGCModal.id)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
                    >
                      Request Consent
                    </button>
                  )}
                  {showUGCModal.consent_status === 'approved' && !showUGCModal.is_featured && (
                    <button
                      onClick={() => updateUGCStatusMutation.mutate({
                        id: showUGCModal.id,
                        data: { is_featured: true },
                      })}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600"
                    >
                      Feature
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowNewFolderModal(false)}
              onKeyDown={(e) => e.key === 'Escape' && setShowNewFolderModal(false)}
              role="button"
              tabIndex={0}
              aria-label="Close modal"
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Folder</h3>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500">
                Will be created at: {currentFolder === '/' ? '' : currentFolder}/{newFolderName || '...'}
              </p>
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setShowNewFolderModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
